export type FormattingFeatureSectionId =
  | "indexation"
  | "constantValueLine"
  | "tradingVolume"
  | "summaryTable"
  | "callOutBoxes"
  | "backgroundShading"
  | "verticalLine"
  | "keyEvents"
  | "illustrativeUnaffectedSharePrice";

export const CHART_FEATURE_FLAG_KEYS = [
  "percentageChangeChart",
  "callOutBoxes",
  "summaryTable",
  "tradingVolume",
  "backgroundShading",
  "constantValueLine",
  "verticalLine",
  "keyEvents",
  "illustrativeUnaffectedSharePrice",
] as const;

export type ChartFeatureFlagKey = (typeof CHART_FEATURE_FLAG_KEYS)[number];

export type ChartFeatureSlotEntry = {
  binary: boolean | null;
  userInput: string;
};

export type ChartFeatureSlotState = Record<ChartFeatureFlagKey, ChartFeatureSlotEntry>;

export function createInitialChartSlotFeatures(): ChartFeatureSlotState {
  return Object.fromEntries(
    CHART_FEATURE_FLAG_KEYS.map((k) => [k, { binary: null, userInput: "" }])
  ) as ChartFeatureSlotState;
}

/** Returns the binary value, or null if unanswered. */
export function chartFeatureSlotToBinary(entry: ChartFeatureSlotEntry): boolean | null {
  return entry.binary;
}

/** Shown when the user tries to enable both features on the same chart. */
export const CALL_OUT_AND_SUMMARY_MUTEX_MESSAGE =
  "Insufficient space on chart. Select either Call Out Boxes or Summary Table. You cannot select both.";

/** Returns whether setting `featureKey` to `binary` is allowed given current `features` (per-chart). */
export function chartFeatureMutexAllowsSelection(
  features: ChartFeatureSlotState,
  featureKey: ChartFeatureFlagKey,
  binary: boolean
): boolean {
  if (!binary) return true;
  if (featureKey !== "callOutBoxes" && featureKey !== "summaryTable") return true;
  const otherKey: ChartFeatureFlagKey = featureKey === "callOutBoxes" ? "summaryTable" : "callOutBoxes";
  return features[otherKey].binary !== true;
}


export type FormattingFeatureOption = {
  dataIndex: string;
  title: string;
  subtitle: string;
  image: string;
};

export type FormattingFeatureSection = {
  id: FormattingFeatureSectionId;
  featureKey: ChartFeatureFlagKey;
  question: string;
  mainQuestion: string;
  options: [FormattingFeatureOption, FormattingFeatureOption];
};

/** Shown only for key-event layouts (`layout_201` / `layout_211`); inserted after indexation. */
export const KEY_EVENTS_FORMATTING_SECTION: FormattingFeatureSection = {
  id: "keyEvents",
  featureKey: "keyEvents",
  question:
    "Do you want to mark key events directly on the chart? Numbered circle markers can be added to selected dates on the time series line, with matching explanatory bullets shown alongside the chart. ",
  mainQuestion:
    "Are there specific events you want marked on the chart? Provide the date and, if available, the text to include in the accompanying bullet. Add up to 9 key event markers.",
  options: [
    {
      dataIndex: "keyEvents",
      title: "Add Key Event Markers",
      subtitle:
        "Adds numbered circle markers to selected dates on the chart, with associated explanatory text.",
      image: "/assets/line_chart/AddKeyEvent.png",
    },
    {
      dataIndex: "noKeyEvents",
      title: "Standard Chart",
      subtitle: "No key event markers will be added to the chart.",
      image: "/assets/line_chart/NoKeyEvent.png",
    },
  ],
};

const FORMATTING_FEATURE_SECTIONS_BASE: FormattingFeatureSection[] = [
  {
    id: "indexation",
    featureKey: "percentageChangeChart",
    question:
      "How should the share price lines be displayed? Showing each company at its actual traded price can make comparisons harder when starting values differ significantly. Rebase the lines to a common starting point to compare performance more directly over time.",
    mainQuestion:
      "Do you want the chart rebased to a common starting value? Choose between the Primary Company's actual share price and a baseline value of 100 for percentage-change comparison",
    options: [
      {
        dataIndex: "percentageChangeChart",
        title: "Rebased Performance Chart",
        subtitle:
          "Each line starts from the same baseline value, making relative performance easier to compare.",
        image: "/assets/line_chart/Index.png",
      },
      {
        dataIndex: "actualTradedValueChart",
        title: "Actual Traded Value Chart",
        subtitle: "Each line is shown at its actual traded share price, with no rebasing applied.",
        image: "/assets/line_chart/noIndex.png",
      },
    ],
  },
  {
    id: "callOutBoxes",
    featureKey: "callOutBoxes",
    question:
      "Do you want to add callout boxes to the chart? Callout boxes can help highlight specific share price moves, events, or inflection points at selected dates.",
    mainQuestion:
      "Are there specific events you want highlighted with callout boxes? Provide the date and, if available, the wording to include. Add up to 5 callout boxes.",
    options: [
      {
        dataIndex: "callOutBoxes",
        title: "Add Callout Boxes",
        subtitle: "Adds selected callout boxes to emphasize specific moments in the share price story.",
        image: "/assets/line_chart/Q4_AddCallOutBoxes.png",
      },
      {
        dataIndex: "noCallOutBoxes",
        title: "No Callout Boxes",
        subtitle: "No callout boxes will be added to the chart.",
        image: "/assets/line_chart/Q4_NoCallOutBoxes.png",
      },
    ],
  },
  {
    id: "summaryTable",
    featureKey: "summaryTable",
    question: "Do you want to include a trading performance summary table? A summary table can help highlight how the Primary Company and selected benchmarks performed over key time periods.",
    mainQuestion:
      "Do you have preferred time periods or benchmarks for the summary table? Specify any performance periods or relative benchmarks you want included.",
    options: [
      {
        dataIndex: "summaryTable",
        title: "Add Summary Table",
        subtitle: "Adds a summary table showing selected performance periods for the company and relevant benchmarks.",
        image: "/assets/line_chart/Q2_AddSummaryTable.png",
      },
      {
        dataIndex: "noSummaryTable",
        title: "No Summary Table",
        subtitle: "No summary table will be added to the chart.",
        image: "/assets/line_chart/Q2_NoSummaryTable.png",
      },
    ],
  },
  {
    id: "tradingVolume",
    featureKey: "tradingVolume",
    question: "Do you want to show trading volume on the chart? Trading volume can be added on the secondary axis as columns to help highlight changes in market activity over time.",
    mainQuestion:"No input box text change needed unless you plan to ask for a custom volume display rule; the current screenshot does not show a prompt beneath this option.",
    options: [
      {
        dataIndex: "tradingVolume",
        title: "Add Trading Volume",
        subtitle: "Adds the Primary Company's trading volume as a column chart on the secondary axis.",
        image: "/assets/line_chart/Q1_AddSecondaryVerticalAxisBarChart.png",
      },
      {
        dataIndex: "noTradingVolume",
        title: "No Trading Volume",
        subtitle: "Trading volume will not be shown on the chart.",
        image: "/assets/line_chart/Q1_NoIndexation.png",
      },
    ],
  },
  {
    id: "backgroundShading",
    featureKey: "backgroundShading",
    question: "Do you want to include background shading on the chart? Background shading can help highlight broader market periods that shaped trading performance, such as COVID volatility or an AI-led rally.",
    mainQuestion:
      "Are there specific time periods and labels you want used for background shading? Add up to 4 shaded periods.",
    options: [
      {
        dataIndex: "backgroundShading",
        title: "Add Background Shading",
        subtitle: "Adds selected background shading periods to highlight broader market phases.",
        image: "/assets/line_chart/Q5_AddBackgroundShading.png",
      },
      {
        dataIndex: "noBackgroundShading",
        title: "No Background Shading",
        subtitle: "No background shading will be added to the chart.",
        image: "/assets/line_chart/Q5_NoBackgroundShading.png",
      },
    ],
  },
  {
    id: "constantValueLine",
    featureKey: "constantValueLine",
    question: "Do you want to add constant value lines to the chart? These dashed horizontal lines can show fixed values such as an offer price, target price, median, or other reference level.",
    mainQuestion:
      "Are there specific values you want shown as constant lines? You can provide a value directly or reference a derived level such as the 52-week high. Add up to 5 constant lines.",
    options: [
      {
        dataIndex: "constantValueLine",
        title: "Add Constant Value Lines",
        subtitle: "Adds horizontal dashed lines at selected values on the primary vertical axis.",
        image: "/assets/line_chart/staticValueLine.png",
      },
      {
        dataIndex: "noConstantValueLine",
        title: "No Constant Value Lines",
        subtitle: "No constant value lines will be added to the chart.",
        image: "/assets/line_chart/NostaticLine.png",
      },
    ],
  },
  {
    id: "verticalLine",
    featureKey: "verticalLine",
    question: "Do you want to add vertical lines to the chart? Vertical lines can help highlight specific dates where trading behaviour changed, such as a major announcement or earnings release.",
    mainQuestion:
      "Are there specific dates and labels you want used for vertical lines? Add up to 5 vertical lines.",
    options: [
      {
        dataIndex: "verticalLine",
        title: "Add Vertical Lines",
        subtitle: "Adds selected vertical lines to highlight important dates on the chart.",
        image: "/assets/line_chart/Q6_AddVerticalLine.png",
      },
      {
        dataIndex: "noVerticalLine",
        title: "No Vertical Lines",
        subtitle: "No vertical lines will be added to the chart.",
        image: "/assets/line_chart/Q6_NoVerticalLine.png",
      },
    ],
  },
  {
    id: "illustrativeUnaffectedSharePrice",
    featureKey: "illustrativeUnaffectedSharePrice",
    question: "Do you want to add an illustrative unaffected share price line? This can show how the Primary Company might have traded from a selected date if it had moved in line with a chosen market or sector proxy.",
    mainQuestion:
      "Do you have an unaffected start date and a proxy to use for the illustrative line? Add up to 5 unaffected price lines.",
    options: [
      {
        dataIndex: "illustrativeUnaffectedSharePrice",
        title: "Illustrative Unaffected Price",
        subtitle: "Adds a reference line showing how the share price may have moved in line with a selected proxy. ",
        image: "/assets/line_chart/AddIllustrativeUnaffectedSharePrice.png",
      },
      {
        dataIndex: "noIllustrativeUnaffectedSharePrice",
        title: "Standard Chart",
        subtitle: "No illustrative unaffected price line will be added to the chart. ",
        image: "/assets/line_chart/NoIllustrativeUnaffectedSharePrice.png",
      },
    ],
  },
];

export function getFormattingFeatureSections(isKeyEvent: boolean): FormattingFeatureSection[] {
  if (!isKeyEvent) return FORMATTING_FEATURE_SECTIONS_BASE;
  const [indexation, ...rest] = FORMATTING_FEATURE_SECTIONS_BASE;
  return [indexation, KEY_EVENTS_FORMATTING_SECTION, ...rest];
}

export function isSlotChartFeaturesComplete(
  features: ChartFeatureSlotState,
  isKeyEvent: boolean
): boolean {
  return getFormattingFeatureSections(isKeyEvent).every((s) => features[s.featureKey].binary !== null);
}

export function isWizardFormattingFeaturesComplete(
  chartCount: 1 | 2 | null,
  chart1Features: ChartFeatureSlotState,
  chart2Features: ChartFeatureSlotState | undefined,
  isKeyEvent: boolean
): boolean {
  if (!chartCount) return false;
  if (!isSlotChartFeaturesComplete(chart1Features, isKeyEvent)) return false;
  if (chartCount === 2) {
    if (chart2Features === undefined || !isSlotChartFeaturesComplete(chart2Features, isKeyEvent))
      return false;
  }
  return true;
}

/**
 * Slide Edit “Chart Customization” reuses the same feature UI as the new-precedent wizard.
 * Treats `chartCount == null` as a single chart (only `chart1` must be complete), matching that step’s layout.
 */
export function isSlideEditFormattingFeaturesComplete(
  chartCount: 1 | 2 | null,
  chartConfig: {
    chart1: { features: ChartFeatureSlotState };
    chart2?: { features: ChartFeatureSlotState };
  },
  isKeyEvent: boolean
): boolean {
  const effectiveCount: 1 | 2 = chartCount ?? 1;
  if (!isSlotChartFeaturesComplete(chartConfig.chart1.features, isKeyEvent)) return false;
  if (effectiveCount === 2) {
    if (chartConfig.chart2 === undefined || !isSlotChartFeaturesComplete(chartConfig.chart2.features, isKeyEvent)) {
      return false;
    }
  }
  return true;
}
